import React from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Theme,
  Button,
  Stack,
} from "@mui/material";
import { X, Pause, Play, RefreshCw } from "lucide-react";
import AudioWaveform from "./AudioWaveform";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import {
  formatTime,
  generateFallbackWaveform,
  WAVEFORM_BAR_COUNT,
} from "@/modules/common/utils/audio.utils";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

interface RecordingEntryDisplayProps {
  recording: RecordingEntry;
  isPlaying: boolean;
  currentPlaybackTime: number;
  onRemove?: (id: string) => void;
  onTogglePlay: () => void;
  theme: Theme;
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  isActivity?: boolean;
  onSeek?: (time: number) => void;
  playbackRate?: number;
  onPlaybackRateChange?: (rate: number) => void;
}

const PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2];

const RecordingEntryDisplay: React.FC<RecordingEntryDisplayProps> = ({
  recording,
  isPlaying,
  currentPlaybackTime,
  onRemove,
  onTogglePlay,
  theme,
  isLoading = false,
  error = null,
  onRefresh,
  isActivity = false,
  onSeek,
  playbackRate,
  onPlaybackRateChange,
}) => {
  const handleWaveformSeek = onSeek
    ? (fraction: number) => {
        if (recording.duration > 0) {
          onSeek(fraction * recording.duration);
        }
      }
    : undefined;

  const handleSpeedClick = onPlaybackRateChange
    ? () => {
        const currentRate = playbackRate ?? 1;
        const idx = PLAYBACK_RATES.indexOf(currentRate);
        const nextIdx = idx === -1 ? 0 : (idx + 1) % PLAYBACK_RATES.length;
        onPlaybackRateChange(PLAYBACK_RATES[nextIdx]);
      }
    : undefined;
   const { isMobile } = useScreenSize();
  const displayDuration = recording.duration > 0 ? recording.duration : 0;
  const progressPercentage =
    displayDuration > 0 ? (currentPlaybackTime / displayDuration) * 100 : 0;

  const MOBILE_BAR_COUNT = 25;
  const barCount = isMobile ? MOBILE_BAR_COUNT : WAVEFORM_BAR_COUNT;

  const fullWaveformLevels =
    recording.waveformData?.length === WAVEFORM_BAR_COUNT
      ? recording.waveformData
      : generateFallbackWaveform(WAVEFORM_BAR_COUNT);

  // Reduce bars on mobile by taking every nth element
  const waveformLevels = isMobile
    ? fullWaveformLevels.filter((_, index) => index % (WAVEFORM_BAR_COUNT / MOBILE_BAR_COUNT) === 0)
    : fullWaveformLevels;

  const highlightedBars = Math.floor(
    (progressPercentage / 100) * barCount,
  );

  if (error) {
    return (
      <Box sx={{ mb: theme.nawy.spacing.xs, height: 48}}>
        <Box
          sx={{
            borderRadius: theme.nawy.radius.xl,
            backgroundColor: theme.nawy.colors.semantic.layer.secondary,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={theme.nawy.spacing.sm}
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.tertiary,
              }}
            >
              Recording expired or unavailable
            </Typography>
            {onRefresh && (
              <Button
                size="small"
                startIcon={<RefreshCw size={14} />}
                onClick={onRefresh}
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.semibold,
                  textTransform: "none",
                }}
              >
                Reload
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: isActivity ? 0 : theme.nawy.spacing.md }}>
      <Box
        sx={{
          position: "relative",
          borderRadius: theme.nawy.radius.xl,
             paddingTop: theme.nawy.spacing.xxs,
             paddingBottom: theme.nawy.spacing.xxs,
             paddingLeft: theme.nawy.spacing.sm,
             paddingRight: theme.nawy.spacing.sm,
            height: 48,
            width: isMobile ? "100%" : isActivity ? "400px" : "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "visible",

          backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
        }}
      >
        {onRemove && (
          <IconButton
            size="small"
            onClick={() => onRemove(recording.id)}
            sx={{
              position: "absolute",
              top: -6,
              right: -6,
              width: 18,
              height: 18,
              borderRadius: theme.nawy.radius.full,
              backgroundColor: theme.nawy.colors.semantic.layer.primaryInverse,
              color: theme.nawy.colors.semantic.layer.primary,
              "&:hover": {
                backgroundColor: theme.nawy.colors.semantic.text.primary,
              },
            }}
          >
            <X size={12} />
          </IconButton>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.sm,
            width: "100%",
            minWidth: 0,
            height: "100%",
          }}
        >
          <IconButton
            onClick={onTogglePlay}
            disabled={isLoading}
            sx={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: theme.nawy.radius.full,
              backgroundColor: theme.nawy.colors.semantic.layer.accent,
              color: theme.nawy.colors.semantic.layer.primary,
              "&:hover": {
                backgroundColor: theme.nawy.colors.semantic.layer.accentDark,
              },
              "&:disabled": {
                backgroundColor: theme.nawy.colors.semantic.layer.secondary,
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : isPlaying ? (
              <Pause size={22} />
            ) : (
              <Play size={22} />
            )}
          </IconButton>

          <AudioWaveform
            levels={waveformLevels}
            highlightedBars={highlightedBars}
            isMobile={isMobile}
            activeColor={theme.nawy.colors.semantic.border.accent}
            inactiveColor={theme.nawy.colors.semantic.border.primary}
            onSeek={handleWaveformSeek}
          />

          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.primary,
              flexShrink: 0,
              minWidth: isPlaying || currentPlaybackTime > 0 ? 80 : 44,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {isPlaying || currentPlaybackTime > 0
              ? `${formatTime(Math.floor(currentPlaybackTime))} / ${formatTime(Math.floor(displayDuration))}`
              : displayDuration > 0
                ? formatTime(Math.floor(displayDuration))
                : "--:--"}
          </Typography>

          {handleSpeedClick && (
            <Button
              size="small"
              onClick={handleSpeedClick}
              sx={{
                flexShrink: 0,
                minWidth: 0,
                px: "10px",
                height: 28,
                borderRadius: theme.nawy.radius.full,
                color: theme.nawy.colors.semantic.text.primary,
                backgroundColor: theme.nawy.colors.semantic.layer.primary,
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                textTransform: "none",
                lineHeight: 1,
                "&:hover": {
                  backgroundColor: theme.nawy.colors.semantic.layer.tertiary,
                },
              }}
            >
              {playbackRate ?? 1}x
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RecordingEntryDisplay;
